export interface ProjectIcon {
  type: 'lucide' | 'image'
  /** Lucide icon name (e.g. 'Rocket', 'Folder') — only when type='lucide' */
  name?: string
  /** Hex color for lucide icon background */
  color?: string
  /** Container shape — defaults to 'rounded' */
  shape?: 'circle' | 'square' | 'rounded'
  /**
   * Inline `data:image/png;base64,…`, set only on an icon that arrived in a
   * SHARE payload. A local project never has this — its bytes live on disk and
   * are read by id. Present means "use these bytes", because the id in a share
   * belongs to the OWNER's store and resolving it here would find nothing, or
   * an unrelated local project's picture.
   */
  dataUrl?: string
  /** Bumped on each re-upload to bust UI cache — only when type='image' */
  imageVersion?: number
}

export interface RecentIcon {
  sha: string
  thumbDataUrl: string
  addedAt: string
}

/**
 * Claude Code's per-session permission mode. Maps to its `--permission-mode`
 * CLI flag. claude 2.1.206 `--help` lists SEVEN choices (was six as of the
 * doc linked below — a seventh, `manual`, shipped since):
 * https://code.claude.com/docs/en/permission-modes
 *   - `default`          → reads only; prompt for everything else
 *   - `plan`             → reads only, planning mode (no edits)
 *   - `dontAsk`          → only pre-approved tools run; everything else denies
 *   - `acceptEdits`      → auto-approve file edits + common fs commands
 *   - `auto`             → classifier-gated; Claude decides what's safe
 *   - `bypassPermissions`→ skip every permission prompt (DANGEROUS — equivalent
 *                          to the legacy `--dangerously-skip-permissions` flag)
 *   - `manual`           → accepted by the CLI (verified live: `--permission-mode
 *                          manual --version` exits 0) but undocumented — no
 *                          description in `--help` or the docs page above, so
 *                          it renders hintless rather than inventing behavior.
 */
export type PermissionMode =
  | 'default'
  | 'plan'
  | 'dontAsk'
  | 'acceptEdits'
  | 'auto'
  | 'bypassPermissions'
  | 'manual'

/**
 * Claude Code's per-session reasoning effort. Maps to its `--effort` CLI flag.
 * Available levels depend on the model — passing an unsupported level just
 * falls back to the model's default. `undefined` → no flag, Claude uses its
 * own default for the chosen model.
 */
export type EffortLevel = 'low' | 'medium' | 'high' | 'xhigh' | 'max'

/** Which CLI the Project Manager drives for a given project. Gemini is a
 *  WORKER model only (see PmWorkerModel) — the PM itself can't run on it
 *  because gemini has no per-spawn MCP injection that's safe for auth (an
 *  ephemeral GEMINI_CLI_HOME mirror would fork oauth_creds.json, whose
 *  refresh tokens are single-use — token rotation in the mirror would burn
 *  the real account's credentials). */
export type PmModel = 'claude' | 'codex' | 'grok'
/** CLIs a PM can spawn/adopt as worker sessions (all five). Kimi is a WORKER
 *  model only — the PM itself can't run on it because the PM orchestrator's
 *  spawn contract needs the resume key synchronously at spawn and headless
 *  (--print) resume semantics are unverified; kimi DOES support per-spawn MCP
 *  injection (--mcp-config), so promoting it later is purely an engine-matrix
 *  task. */
export type PmWorkerModel = 'claude' | 'codex' | 'grok' | 'gemini' | 'kimi'
/** How the PM's own CLI session runs — an interactive PTY or a headless (non-interactive) process. */
export type PmEngineMode = 'pty' | 'headless'

export interface PmMessage {
  id: string
  role: 'user' | 'assistant' | 'action' | 'event' | 'error'
  /** markdown for user/assistant/error; JSON payload rendered as an action card for 'action'; status line for 'event' */
  text: string
  /** for role='action': which tool ran */
  tool?: string
  /** for role='action': tool args snapshot (folderId/sessionId/model/task…) */
  meta?: Record<string, unknown>
  createdAt: string
}

export interface PmOwnedSession {
  id: string
  model: PmWorkerModel
  cwd: string
  /** '' until the watcher backfills it after spawn */
  resumeKey: string
  name: string
}

export interface PmState {
  projectId: string
  model: PmModel
  engineMode: PmEngineMode
  /** resume key of the PM's own CLI session ('' until first spawn) */
  resumeKey: string
  messages: PmMessage[]
  ownedFolderIds: string[]
  ownedSessions: PmOwnedSession[]
  /** sessions the PM adopted (user-started) via send_followup/get_session_status/get_session_result — get worker events too */
  trackedSessions: PmOwnedSession[]
  /** last worker event delivered per owned session id — used for restart rehydration */
  lastEventBySession: Record<string, string>
  /** true while the PM loop is stopped via the Stop button */
  stopped: boolean
  /** Which PREAMBLE_VERSION (electron/main/pm-orchestrator.ts) this PM's identity was last established under. */
  preambleVersion?: number
}

/**
 * Per-agent default knobs (model / reasoning effort / permission mode /
 * account), shared by the global settings, per-project, and per-folder scopes.
 * Every field is optional — `undefined` means "inherit from the next scope up",
 * and ultimately the CLI's own default. Ids are capability ids (from
 * agent-capabilities), so they stay valid across CLI versions without a schema.
 */
export interface AgentDefaultsEntry {
  model?: string
  effortLevel?: string
  permissionMode?: string
  accountId?: string
}

/**
 * The three-agent bundle of {@link AgentDefaultsEntry} stored on a `Project`
 * or `Folder`. Introduced to make Codex/Grok defaults first-class alongside
 * Claude's. Claude's pre-existing FLAT fields (`defaultPermissionMode` /
 * `defaultEffortLevel` / `defaultAccountId` on Project; `permissionMode` /
 * `effortLevel` / `model` / `accountId` on Folder) are still read as a
 * fallback by `resolveAgentDefaults` (see lib/agentDefaults.ts) — new writes go
 * to `agentDefaults.claude`, old projects keep working. See that module for the
 * migration contract.
 */
export interface AgentDefaults {
  claude?: AgentDefaultsEntry
  codex?: AgentDefaultsEntry
  grok?: AgentDefaultsEntry
  gemini?: AgentDefaultsEntry
  kimi?: AgentDefaultsEntry
}

export interface Project {
  id: string
  name: string
  workingDirectory: string
  color: string
  /** Custom icon — falls back to color dot if absent */
  icon?: ProjectIcon
  createdAt: string
  /**
   * Per-agent defaults (Claude/Codex/Grok) for new sessions in this project.
   * Folders may override per-folder. Resolved via `resolveAgentDefaults`, which
   * also falls back to the legacy flat Claude fields below for `claude`.
   */
  agentDefaults?: AgentDefaults
  /**
   * @deprecated Legacy flat Claude default — still READ as a fallback by
   * `resolveAgentDefaults('claude', …)`; new writes go to
   * `agentDefaults.claude.permissionMode`.
   * Default permission mode for new Claude sessions in this project. Folders
   * may override per-folder. `undefined` → fall back to Claude's `default`
   * mode (or the legacy global `claudeDangerouslySkipPermissions` setting).
   */
  defaultPermissionMode?: PermissionMode
  /**
   * @deprecated Legacy flat Claude default — read as a fallback; new writes go
   * to `agentDefaults.claude.effortLevel`.
   * Default reasoning effort for new Claude sessions in this project. Folders
   * may override. `undefined` → fall back to the global setting / Claude's
   * model default.
   */
  defaultEffortLevel?: EffortLevel
  /**
   * @deprecated Legacy flat Claude default — read as a fallback; new writes go
   * to `agentDefaults.claude.accountId`.
   * Default Claude account id for new sessions in this project (folders/sessions
   * may override). `undefined` → fall back to the global default, then 'default'.
   */
  defaultAccountId?: string
  /** Model the Project Manager uses for this project. `undefined` → defaults to 'claude'. */
  pmModel?: PmModel
  /** Engine mode the Project Manager uses for this project. `undefined` → defaults to 'pty'. */
  pmEngineMode?: PmEngineMode
}

export interface Folder {
  id: string
  projectId: string
  resumeKey: string
  name: string
  branch?: string
  worktreePath?: string
  isWorkspace?: boolean
  status: '' | 'active' | 'working' | 'waiting'
  createdAt: string
  lastActiveAt: string
  takenOverFrom?: string
  /**
   * Default whitelist of MCP server names enabled for new Claude sessions
   * spawned in this folder.
   *   - `undefined` → all MCPs enabled (legacy / first-time behavior)
   *   - `[]`        → all MCPs disabled
   *   - `[...]`     → only these MCP names enabled
   * The session's actual launch arguments are still resolved at spawn time
   * from the user's current selection — this field is only the *seed* for
   * the MCP picker on the "What would you like to work on?" input.
   */
  defaultEnabledMcps?: string[]
  /**
   * Per-agent (Claude/Codex/Grok) override for sessions spawned in this folder.
   * Highest-priority scope in `resolveAgentDefaults`. Claude's `claude` entry
   * falls back to the legacy flat fields below (`permissionMode` / `effortLevel`
   * / `model` / `accountId`); new writes go here.
   */
  agentDefaults?: AgentDefaults
  /**
   * @deprecated Legacy flat Claude override — read as a fallback; new writes go
   * to `agentDefaults.claude.permissionMode`.
   * Per-folder permission mode override. When `undefined`, the spawn falls
   * back to the project's `defaultPermissionMode`, and ultimately to Claude's
   * own `default`. Use `'bypassPermissions'` to skip every prompt for sessions
   * spawned in this folder (replaces the deprecated
   * `--dangerously-skip-permissions` global toggle).
   */
  permissionMode?: PermissionMode
  /**
   * Per-folder reasoning effort override. When `undefined`, falls back to the
   * project's `defaultEffortLevel`, then the global setting, then Claude's
   * model default.
   */
  effortLevel?: EffortLevel
  /**
   * Per-folder Claude model override (`--model <alias|id>`). When `undefined`,
   * Claude uses its own default. Also seeds the model picker's last-used value.
   */
  model?: string
  /**
   * The Claude account this folder's sessions run as. Resolved at first launch
   * (session override → folder → project → global → 'default') and stored here so
   * resume reuses the same account. `undefined` → resolve from higher scopes.
   */
  accountId?: string
  /** True when the Project Manager spawned this folder — drives PM-owned UI badges/cleanup. */
  createdByPm?: boolean
}

export interface CodexSession {
  id: string
  projectId: string
  resumeKey: string
  name: string
  status: '' | 'active' | 'working' | 'waiting'
  createdAt: string
  lastActiveAt: string
  /** Which Codex account this session runs as (undefined = default). */
  accountId?: string
  /** Model this session runs as (`-m <model>`); undefined = Codex default. */
  model?: string
  /** Reasoning effort (`-c model_reasoning_effort=…`); undefined = model default. */
  effortLevel?: string
  /**
   * Permission mode id from agent-capabilities (`read-only` | `workspace-ask`
   * | `workspace-auto` | `full-access` — curated approval_policy×sandbox_mode
   * combos, mapped to real flags in agent-spawn-args). Passed on create AND
   * resume so the session keeps its launch semantics; undefined = Codex default.
   */
  permissionMode?: string
  /**
   * Whitelist of MCP server names enabled for this session (fresh spawns only —
   * resume keeps the original set). `undefined` = inherit the CLI's default MCP
   * set (no filtering). Enforced on spawn per-CLI: codex `-c
   * mcp_servers.<name>.enabled=false`, grok ephemeral-home filtered config.toml,
   * gemini `--allowed-mcp-server-names`, kimi `--mcp-config`.
   */
  enabledMcps?: string[]
  /** True when the Project Manager spawned this session — drives PM-owned UI badges/cleanup. */
  createdByPm?: boolean
}

export interface GrokSession {
  id: string
  projectId: string
  resumeKey: string
  name: string
  status: '' | 'active' | 'working' | 'waiting'
  createdAt: string
  lastActiveAt: string
  /** Which Grok account this session runs as (undefined = default). */
  accountId?: string
  /** Model this session runs as (`-m <model>`); undefined = Grok default. */
  model?: string
  /** Reasoning effort (`--reasoning-effort …`); undefined = model default. */
  effortLevel?: string
  /**
   * Permission mode id (`--permission-mode …` — same six values as Claude's
   * PermissionMode per `grok --help`). Passed on create AND resume; undefined
   * = Grok default.
   */
  permissionMode?: string
  /**
   * Whitelist of MCP server names enabled for this session (fresh spawns only —
   * resume keeps the original set). `undefined` = inherit the CLI's default MCP
   * set (no filtering). Enforced on spawn per-CLI: codex `-c
   * mcp_servers.<name>.enabled=false`, grok ephemeral-home filtered config.toml,
   * gemini `--allowed-mcp-server-names`, kimi `--mcp-config`.
   */
  enabledMcps?: string[]
  /** True when the Project Manager spawned this session — drives PM-owned UI badges/cleanup. */
  createdByPm?: boolean
}

export interface GeminiSession {
  id: string
  projectId: string
  resumeKey: string
  name: string
  status: '' | 'active' | 'working' | 'waiting'
  createdAt: string
  lastActiveAt: string
  /** Which Gemini account this session runs as (undefined = default). */
  accountId?: string
  /** Model this session runs as (`-m <model>`); undefined = Gemini default. */
  model?: string
  /** Unused — gemini has no reasoning-effort flag. Kept for record-shape parity. */
  effortLevel?: string
  /**
   * Approval mode id (`--approval-mode default|auto_edit|yolo|plan` — gemini's
   * own four-value enum). Passed on create AND resume; undefined = Gemini
   * default.
   */
  permissionMode?: string
  /**
   * Whitelist of MCP server names enabled for this session (fresh spawns only —
   * resume keeps the original set). `undefined` = inherit the CLI's default MCP
   * set (no filtering). Enforced on spawn per-CLI: codex `-c
   * mcp_servers.<name>.enabled=false`, grok ephemeral-home filtered config.toml,
   * gemini `--allowed-mcp-server-names`, kimi `--mcp-config`.
   */
  enabledMcps?: string[]
  /** True when the Project Manager spawned this session — drives PM-owned UI badges/cleanup. */
  createdByPm?: boolean
}

export interface KimiSession {
  id: string
  projectId: string
  resumeKey: string
  name: string
  status: '' | 'active' | 'working' | 'waiting'
  createdAt: string
  lastActiveAt: string
  /** Which Kimi account this session runs as (undefined = default). */
  accountId?: string
  /** Model this session runs as (`-m <config-model-key>`, e.g.
   *  "kimi-code/kimi-k2.5"); undefined = Kimi's configured default. */
  model?: string
  /** Kimi's only reasoning control: 'thinking' | 'no-thinking' →
   *  `--thinking`/`--no-thinking`; undefined = config default. */
  effortLevel?: string
  /**
   * Approval mode id ('default' | 'plan' | 'yolo' | 'afk' → no flag /
   * `--plan` / `--yolo` / `--afk`). Passed on create AND resume; undefined =
   * Kimi default (ask per tool call).
   */
  permissionMode?: string
  /**
   * Whitelist of MCP server names enabled for this session (fresh spawns only —
   * resume keeps the original set). `undefined` = inherit the CLI's default MCP
   * set (no filtering). Enforced on spawn per-CLI: codex `-c
   * mcp_servers.<name>.enabled=false`, grok ephemeral-home filtered config.toml,
   * gemini `--allowed-mcp-server-names`, kimi `--mcp-config`.
   */
  enabledMcps?: string[]
  /** True when the Project Manager spawned this session — drives PM-owned UI badges/cleanup. */
  createdByPm?: boolean
}

/** One selectable model for a CLI, from agent-capabilities live discovery. */
export interface AgentModel {
  id: string
  label: string
  default?: boolean
}
/** One selectable reasoning-effort level for a CLI. */
export interface AgentEffort {
  id: string
  label: string
  default?: boolean
}
/** One selectable permission mode for a CLI (curated per CLI version — none of
 *  the three CLIs enumerates these live). */
export interface AgentPermission {
  id: string
  label: string
  hint?: string
  default?: boolean
}
export interface AgentCapability {
  installed: boolean
  version: string | null
  models: AgentModel[]
  efforts: AgentEffort[]
  permissions: AgentPermission[]
}
export interface AgentCapabilities {
  claude: AgentCapability
  codex: AgentCapability
  grok: AgentCapability
  gemini: AgentCapability
  kimi: AgentCapability
}

export interface DiscoveredAgent {
  id: string
  sessionId: string
  workingDirectory: string
  model: string
  status: 'running' | 'idle' | 'finished'
  conversationPreview: string[]
  lastActivity: string
  /**
   * Label derived from the transcript's opening prompt, for sessions CodeFlows
   * has never named (started from a terminal, by a subagent, or before this
   * project was added). A CodeFlows name always wins over this.
   */
  title?: string
}

export type TabType = 'claude' | 'file' | 'browser' | 'discovery' | 'settings' | 'app-settings' | 'stats' | 'note' | 'terminal' | 'git' | 'request' | 'webhook' | 'apps' | 'aitables' | 'aiboards' | 'basecamp' | 'codex' | 'grok' | 'gemini' | 'kimi' | 'projectManager' | 'shared'

/**
 * One attached selection in a Claude session's input area, displayed as a
 * chip above the prompt. Sourced from many places (file editor, terminal
 * selection, AiTables row, AiBoards capture, Basecamp card, …).
 *
 * `file` is both the unique key (dedupe is by file+startLine+endLine) and
 * historically what was rendered on the chip — but newer chips with custom
 * icons + friendly labels override the rendering with `displayLabel` /
 * `source`. The MCP bridge still uses `file` for code-style selections so
 * keep it sensible there.
 *
 * Optional `source` + `displayLabel` are populated for the new app-sourced
 * chips (AiTables, AiBoards, Basecamp). When `source` is set, the chip
 * renders the matching app icon and uses `displayLabel` instead of `file`.
 *
 * Optional `tempFilePath` points at a temp file the host should delete
 * when the chip is consumed by the MCP bridge OR removed by the user.
 * Used by AiBoards captures so we don't leak PNGs forever.
 */
export interface PendingSelection {
  file: string
  text: string
  startLine: number
  endLine: number
  /** Tab id this selection originated from — drives "go back to source" on chip click. */
  sourceTabId?: string
  /** Source app — drives the chip icon. Absent for file-editor selections. */
  source?: 'aitables' | 'aiboards' | 'basecamp'
  /** Pretty label shown on the chip when `source` is set. */
  displayLabel?: string
  /** Temp file path to delete on consume / remove. AiBoards captures only. */
  tempFilePath?: string
  /** AiTables: source row UUID for highlight bridge. */
  rowId?: string
  /** AiBoards: captured Konva element UUIDs for highlight bridge. */
  boardElementIds?: string[]
}

/**
 * Data for a `shared` tab — something someone shared with you.
 *
 * Defined in lib/shareTabs.ts, next to the pure helpers that build and read
 * it, so the two can never drift; re-exported here so the Tab union stays
 * complete. The import is type-only in both directions and is fully erased
 * at build time, so there is no runtime cycle.
 */
export type { SharedTabData } from './lib/shareTabs'

export interface Tab {
  id: string
  type: TabType
  projectId: string
  folderId: string
  title: string
  icon?: string
  data: ClaudeTabData | FileTabData | BrowserTabData | DiscoveryTabData | SettingsTabData | AppSettingsTabData | UsageStatsTabData | NoteTabData | TerminalTabData | GitTabData | RequestTabData | WebhookTabData | AppsTabData | AiTablesTabData | AiBoardsTabData | BasecampTabData | CodexTabData | GrokTabData | GeminiTabData | KimiTabData | ProjectManagerTabData | import('./lib/shareTabs').SharedTabData
  closable: boolean
}

export interface ClaudeTabData {
  sessionId: string
  resumeKey: string
  worktreePath?: string
  /**
   * Newest terminal title Claude emitted (OSC 0/2), spinner glyph stripped.
   * Recorded on every change even when the label isn't repainted, so the tab
   * still knows what the CLI last called itself.
   */
  terminalTitle?: string
  /**
   * Set when the user renames this tab by hand — from then on the label is
   * theirs and Claude's titles are recorded but never displayed. Explicit
   * rather than inferred: see lib/terminalTitle.ts.
   */
  titleLocked?: boolean
}

export interface FileTabData {
  filePath: string
  language: string
}

export interface BrowserTabData {
  url: string
  port: number
}

export interface DiscoveryTabData {
  projectId: string
  /** Which tool this session-start page launches. Defaults to 'claude'. */
  tool?: 'claude' | 'codex' | 'grok' | 'gemini' | 'kimi'
}

export interface SettingsTabData {
  projectId: string
}

export interface AppSettingsTabData {
  _brand?: 'app-settings'
}

export interface UsageStatsTabData {
  _brand?: 'stats'
}

export interface SidebarApp {
  id: string
  appId: string
  projectId: string
  title: string
}

export interface NoteTabData {
  noteId: string
  filePath: string
}

export interface TerminalTabData {
  terminalId: string
}

export interface CodexTabData {
  sessionId: string
  resumeKey: string
}

export interface GrokTabData {
  sessionId: string
  resumeKey: string
}

export interface GeminiTabData {
  sessionId: string
  resumeKey: string
}

export interface KimiTabData {
  sessionId: string
  resumeKey: string
}

export interface ProjectManagerTabData {
  projectId: string
}

export interface GitTabData {
  projectId: string
}

export interface RequestTabData {
  requestId: string
  importedRequest?: {
    method: string
    url: string
    headers: { key: string; value: string }[]
    body?: string
  }
}

export interface WebhookTabData {
  /** Server-issued endpoint UUID. Empty until the panel provisions one. */
  endpointId?: string
  /** Server-issued auth secret for management ops. Stored locally; never re-fetchable. */
  secret?: string
  /** Full receive URL: <baseUrl>/hook/<endpointId>. */
  webhookUrl?: string
  /** Worker base URL captured at provision time. */
  baseUrl?: string
}

/** App gallery — picker for adding any registered app + favorites management. */
export interface AppsTabData {
  /** Optional initial search query when the gallery opens. */
  initialQuery?: string
}

/** AiTables — first-party Add.AI tables web app. Currently a thin webview wrapper
 *  around tables.add.ai; richer integration will land in future PRs. */
export interface AiTablesTabData {
  /** Reserved for future deep-link / state restoration. */
  initialPath?: string
  /** Last URL the user navigated to inside the AiTables webview. Persisted
   *  so reopening the tab restores the same view, and so chip click handlers
   *  can decide whether to navigate before highlighting. */
  currentUrl?: string
}

/** AiBoards — first-party Add.AI boards web app. */
export interface AiBoardsTabData {
  initialPath?: string
  /** Last URL the user navigated to inside the AiBoards webview. */
  currentUrl?: string
}

/** Basecamp — 37signals project management web app. */
export interface BasecampTabData {
  /** Reserved for future deep-linking into a specific project / card. */
  initialPath?: string
  /** Last URL the user navigated to inside the Basecamp webview. */
  currentUrl?: string
}

/**
 * Which embedded app / subsystem produced a notification. Adding a new
 * source = adding one entry here + one entry in `DEFAULT_NOTIFICATION_PREFS`
 * below + one entry in `NOTIFICATION_SOURCE_META` in
 * `app/src/lib/notifications.ts`. Everything else — settings UI, bell
 * popover, cross-dismissal — picks up new sources automatically because
 * they iterate this list.
 */
export type NotificationSource = 'basecamp' | 'claude' | 'codex' | 'grok' | 'gemini' | 'kimi' | 'git'

/**
 * Chat providers the user can open as tabs in the right-side chat
 * panel. At most one of each — the picker hides providers already
 * open. `codeflows` is the native CodeFlows chat list; the others
 * render external webviews.
 */
export type ChatProvider = 'codeflows' | 'chatflows' | 'teams' | 'basecamp'

/**
 * Per-source destination toggles. `computer` = fire a native OS toast
 * via the Electron Notification API (the thing you see in macOS
 * Notification Center). `app` = add an entry to the in-app bell
 * popover. Both default on/off per source based on what makes sense
 * for that source (Basecamp: app only, the user explicitly asked to
 * silence the OS toasts; Claude: both, matches existing behaviour).
 */
export interface NotificationDestinationPrefs {
  computer: boolean
  app: boolean
}

export type NotificationPrefs = Record<NotificationSource, NotificationDestinationPrefs>

/**
 * First-launch defaults — also used to fill in missing keys when
 * hydrating from persisted settings (so adding a new source doesn't
 * crash on a stale settings blob).
 */
export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  // Basecamp's OS toasts are noise (every ping would spawn one),
  // and the dock badge is actively undesired — bell only by default.
  // Flip `computer` on from settings to get OS toasts.
  basecamp: { computer: false, app: true },
  // Claude session events (complete / waiting) fire in both places
  // by default — the OS toast catches you when the window is hidden,
  // the bell entry is there when you come back.
  claude:   { computer: true,  app: true },
  // Codex / Grok / Gemini session events mirror Claude's defaults.
  codex:    { computer: true,  app: true },
  grok:     { computer: true,  app: true },
  gemini:   { computer: true,  app: true },
  kimi:     { computer: true,  app: true },
  // Git auto-fetch — opt-in feature, both surfaces on by default since
  // the user explicitly enables it from Settings.
  git:      { computer: true,  app: true },
}

/**
 * In-app notification surfaced in the TitleBar bell popover. Covers
 * BOTH live unread-count mirrors (Basecamp's Pings/Hey!/Activity) AND
 * transient event notifications (Claude session-complete /
 * session-waiting). Discriminator is the `source` field.
 *
 * Entries are keyed by a stable id of the form `${source}:${kind}:
 * ${refId?}` — refId is the sessionId for Claude and omitted for
 * Basecamp (Basecamp aggregates across tabs so there's exactly one
 * "pings" entry regardless of how many tabs are scanning). The same
 * id is passed as the OS-toast click reference so clicking a native
 * notification can find + remove the matching in-app entry
 * (cross-dismissal).
 */
export interface AppNotification {
  /** Stable dedup / click-ref key. See comment above for format. */
  id: string
  /** Which embedded app produced this notification. */
  source: NotificationSource
  /** Channel within the source (e.g. 'pings' / 'session-waiting'). */
  kind: string
  /**
   * Human-readable headline — "1 new ping", "Session finished", etc.
   * Used as the primary label in both the bell row AND the OS toast
   * title so both surfaces tell the same story.
   */
  title: string
  /** Optional secondary line (e.g. "needs your help"). */
  body?: string
  /**
   * Aggregate count for count-based notifications (Basecamp pings). 0
   * or absent for event-based notifications (Claude session events).
   */
  count?: number
  /**
   * URL to navigate to when the user clicks the notification. For
   * Basecamp: the top-nav link (/circles, /my/readings). For Claude:
   * absent — navigation resolves to a session, not a URL.
   */
  url?: string
  /**
   * Source tab id, if applicable. For Basecamp: the tab whose scanner
   * most recently reported the count — used as a hint for the click
   * handler (which picks the best target tab dynamically). For Claude:
   * absent — clicks resolve via sessionId instead.
   */
  tabId?: string
  /**
   * Session id, if applicable. For Claude: the session the event came
   * from — clicking focuses that session across projects. For Basecamp:
   * absent.
   */
  sessionId?: string
  /** First time we saw this (source, kind) combination. */
  firstSeenTs: number
  /** Most recent update — used for sort order + relative time display. */
  updatedTs: number
  /** Flipped to true when the user clicks the notification or hits "mark all read". */
  read: boolean
}

/**
 * Voice input — recorded audio is transcribed by the
 * `codeflows_voice_transcribe` Supabase edge function (Whisper via a
 * server-side OpenAI key), then optionally rewritten by piping the
 * transcript through `claude -p <profile.prompt>`. The built-in `Raw`
 * profile (id: 'raw') has an empty prompt; the renderer skips the
 * `claude -p` step for Raw and inserts the transcript directly.
 *
 * `defaultProfileId` applies everywhere unless overridden in
 * `perApp[appKey]`. Switching the profile inline from a surface always
 * writes `perApp[appKey]` — never mutates `defaultProfileId`. The
 * Settings UI is the only place to change the global default.
 */
export type VoiceAppKey = 'claude' | 'codex' | 'grok' | 'gemini' | 'kimi' | 'git'

export interface VoiceProfile {
  id: string
  name: string
  prompt: string
}

export interface VoiceSettings {
  profiles: VoiceProfile[]
  defaultProfileId: string
  perApp: Partial<Record<VoiceAppKey, string>>
  /**
   * Electron accelerator string (e.g. `CommandOrControl+Shift+V`) that
   * toggles voice recording on whichever surface (Claude/Codex/Git/Add
   * Claude) is currently visible. `null` = no shortcut bound.
   */
  keybind?: string | null
}

export const RAW_VOICE_PROFILE: VoiceProfile = {
  id: 'raw',
  name: 'Raw',
  prompt: '',
}

export const DEFAULT_VOICE_SETTINGS: VoiceSettings = {
  profiles: [RAW_VOICE_PROFILE],
  defaultProfileId: 'raw',
  perApp: {},
  keybind: null,
}

export interface GitFileChange {
  path: string
  status: 'added' | 'modified' | 'deleted' | 'renamed' | 'untracked' | 'conflicted'
  staged: boolean
  oldPath?: string
}

export interface GitBranch {
  name: string
  current: boolean
  remote?: string
  ahead?: number
  behind?: number
}

export interface GitLogEntry {
  hash: string
  shortHash: string
  parents: string[]
  message: string
  author: string
  authorEmail: string
  date: string
  refs: string[]
  body: string
}

export interface GitStashEntry {
  index: number
  message: string
}

// Split View
export interface SplitPanel {
  id: string
  tabIds: string[]
  activeTabId: string | null
}

export type SplitLayout =
  | '2-col'              // [1 | 2]
  | '2-row'              // [1] / [2]
  | '3-col'              // [1 | 2 | 3]
  | '2-col-1-bottom'     // [1 | 2] / [3]
  | '1-top-2-col-bottom' // [1] / [2 | 3]
  | '2x2'                // [1 | 2] / [3 | 4]

export interface SplitViewState {
  id: string
  layout: SplitLayout
  panels: SplitPanel[]
  activePanelId: string | null
  horizontalRatio: number // top/bottom row height split (%)
  verticalRatio: number   // left/right column width split (%)
  name: string
}

// ── getCurrentSplitView MCP tool snapshot (renderer → main → MCP server) ───
// This is the shape written to ~/tmp/codeflows-state/<projectHash>.json
// under the `splitView` key and read by codeflows-mcp-server.mjs. See
// docs/superpowers/specs/2026-04-09-getcurrentsplitview-mcp-tool-design.md
// for the full contract and design rationale.

export interface SplitViewPanelTab {
  id: string
  type: TabType
  title: string
  /** Web-app panels: the live URL inside the <webview>. */
  currentUrl?: string
  // AiTables
  workspaceSlug?: string | null
  workspaceId?: string | null
  workspaceName?: string | null
  databaseId?: string | null
  databaseName?: string | null
  tableId?: string | null
  tableName?: string | null
  viewId?: string | null
  // AiBoards (reuses workspace* fields above)
  boardId?: string | null
  boardName?: string | null
  // Basecamp
  accountId?: string | null
  accountName?: string | null
  projectId?: string | null
  projectName?: string | null
  projectType?: string | null
  pageType?: string | null
  resourceType?: string | null
  resourceId?: string | null
  resourceName?: string | null
  // File
  filePath?: string
  language?: string
  // Browser
  url?: string
  port?: number
  // Session
  sessionId?: string
  resumeKey?: string
  worktreePath?: string
  // Terminal
  terminalId?: string
  // Git
  gitProjectId?: string
  // Note
  noteId?: string
  noteFilePath?: string
}

export interface SplitViewPanelSnapshot {
  id: string
  isActive: boolean
  tab: SplitViewPanelTab
}

export interface SplitViewSnapshot {
  updatedAt: number
  present: boolean
  // Only set when present=true
  id?: string
  name?: string
  layout?: SplitLayout
  activePanelId?: string | null
  panels?: SplitViewPanelSnapshot[]
  // Only set when present=false — single active tab context
  activeTab?: SplitViewPanelTab
}

export type OverlayState = 'off' | 'visible' | 'hidden'

export type SidebarState = 'expanded' | 'collapsed' | 'hidden'

/**
 * Where a link clicked inside CodeFlows opens (Settings → Browser).
 *
 * 'in-app'  — a CodeFlows browser tab, which is what every link did before
 *             the setting existed, and so stays the default.
 * 'system'  — the machine's default browser, via `window.open`, which the
 *             main process turns into `shell.openExternal`.
 */
export type LinkTarget = 'in-app' | 'system'

export type ShellType = 'zsh' | 'bash' | 'powershell' | 'cmd'

export interface TerminalInstance {
  id: string
  projectId: string
  name: string
  shell: ShellType
  createdAt: string
}

export interface NoteFile {
  id: string
  name: string
  filePath: string
}

export interface McpServer {
  name: string
  transport: 'stdio' | 'http' | 'sse'
  command: string
  url?: string
  headers?: Record<string, string>
  malformed?: 'remote-as-stdio'
  malformedUrl?: string
  args: string[]
  env: Record<string, string>
  scope: 'global' | 'project'
  enabled: boolean
  registryId?: string
  registryVersion?: string
  packageId?: string
  color?: string
  icon?: string
  version?: string
  readme?: string
}

export type McpAuthCli = 'claude' | 'codex' | 'grok' | 'gemini' | 'kimi'
export type McpAuthState = 'authed' | 'needs-auth' | 'unsupported' | 'cli-missing'

export type McpLoginState =
  | { cli: 'claude' | 'codex'; phase: 'starting' }
  | { cli: 'claude' | 'codex'; phase: 'awaitingBrowser'; url?: string }
  | { cli: 'claude' | 'codex'; phase: 'success' }
  | { cli: 'claude' | 'codex'; phase: 'error'; message: string }

/**
 * Emitted when a Claude session spawns under an account that has a remote
 * MCP server configured but no OAuth token for it. `accountId` is passed
 * through unmodified from the main process — `undefined` means the default
 * account; an empty string is a distinct (if unusual) bucket of its own, so
 * never coerce it with `?? ''` or `|| ''` on the way through.
 *
 * `ptyId` is the id `claudeManager.spawnClaude` generated for the session
 * that triggered this check — the same id the renderer already tracks in
 * `useAppStore`'s `ptyMap[session.id]`. Join on it to scope a banner to the
 * tab actually running the affected session; the account name alone isn't
 * enough since another visible tab may belong to a different account.
 */
export interface McpAuthAccountGap {
  accountId?: string
  ptyId: string
  servers: Array<{ name: string; url: string }>
}

export interface Skill {
  slug: string
  name: string
  description: string
  body: string
  rawContent: string
  scope: 'global' | 'project'
  directory: string
  files: Array<{ path: string; size: number }>
  registryId?: string
  registryVersion?: string
  packageId?: string
  color?: string
  icon?: string
  version?: string
  readme?: string
}

/** @deprecated Use Skill. Kept as an alias for backwards compat. */
export type SkillCommand = Skill

export interface SlashCommand {
  name: string
  description: string
  content: string
  scope: 'global' | 'project'
  filePath: string
  registryId?: string
}

// === AI Sync (Claude ↔ Codex ↔ Grok) ====================================
export interface McpSyncEntry {
  name: string
  scope: 'global' | 'project'
  inClaude: boolean
  inCodex: boolean
  inGrok: boolean
  codeflowsManaged: boolean
  inSync: boolean
  conflict: boolean
  /** false only for entries with neither a command nor a url — there is
   *  nothing to write. Remote (http/sse) servers ARE syncable. */
  syncable: boolean
  /** Transport of the base entry, for labelling — independent of `syncable`. */
  transport: 'stdio' | 'http' | 'sse'
  /** npm package the server runs via npx (null → custom command, no version ops). */
  npmPackage: string | null
  claudeVersion: string | null
  codexVersion: string | null
  grokVersion: string | null
}

export interface SkillSyncEntry {
  slug: string
  scope: 'global' | 'project'
  inAgents: boolean
  inClaude: boolean
  inCodex: boolean
  inGrok: boolean
  isLinked: boolean
  codeflowsManaged: boolean
  conflict: boolean
  claudeVersion: string | null
  codexVersion: string | null
  grokVersion: string | null
}

export interface CommandSyncEntry {
  name: string
}

export interface SyncStatus {
  mcps: McpSyncEntry[]
  skills: SkillSyncEntry[]
  commands: CommandSyncEntry[]
}

export interface RegistryMcp {
  id: string
  name: string
  slug: string
  description: string
  command: string
  args: string[]
  transport?: 'stdio' | 'http' | 'sse'
  url?: string
  env_vars: Array<{ key: string; label: string; required: boolean }>
  icon: string
  category: string
  documentation_url: string | null
  npm_package: string | null
  readme?: string | null
  version?: string | null
  repository_url?: string | null
  download_count?: number | null
  logo_url?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export interface RegistrySkill {
  id: string
  name: string
  slug: string
  description: string
  content: string
  files: Array<{ path: string; content: string }>
  icon: string
  category: string
  readme?: string | null
  version?: string | null
  repository_url?: string | null
  download_count?: number | null
  logo_url?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export interface RegistryPackage {
  id: string
  name: string
  slug: string
  description: string
  icon: string
  category: string
  skill_ids: string[]
  mcp_ids: string[]
  readme?: string | null
  version?: string | null
  repository_url?: string | null
  download_count?: number | null
  logo_url?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export interface RegistryPackageWithContents extends RegistryPackage {
  skills: RegistrySkill[]
  mcps: RegistryMcp[]
}

export interface InstalledPackage {
  id: string
  slug: string
  name: string
  description: string
  scope: 'global' | 'project'
  installedAt: string
}

export interface FileNode {
  name: string
  path: string
  type: 'file' | 'directory'
  children?: FileNode[]
  extension?: string
  gitIgnored?: boolean
}

export interface PortInfo {
  port: number
  pid: number
  command: string
}

// ---- Claude usage widget (mirrors electron/main/claudeUsage.helpers.ts) ----
export interface UsageMetricObj { utilization: number; resets_at: string | null }
export interface UsageResponse { [key: string]: unknown; extra_usage?: unknown }
export interface UsageAccount { id: string; isDefault: boolean; orgName: string | null; email: string | null }
export interface UsageSnapshot { t: number; metrics: Record<string, number>; resets?: Record<string, string | null> }

/** Local usage stats parsed from session transcripts (codeflows sessions). */
export interface LocalStatBlock {
  inputTokens: number
  outputTokens: number
  cacheCreateTokens: number
  cacheReadTokens: number
  costUsd: number
  prompts: number
  assistantTurns: number
  sessions: number
  webSearches: number
  webFetches: number
  toolCalls: Record<string, number>
  byModel: Record<string, { tokens: number; costUsd: number }>
  byProject: Record<string, { tokens: number; prompts: number; sessions: number; costUsd: number }>
  series: Array<{ t: number; tokens: number; prompts: number }>
  bucketMs: number
}
export interface LocalStats {
  today: LocalStatBlock
  yesterday: LocalStatBlock
  week: LocalStatBlock
  generatedAt: number
  fx?: Record<string, number>
  currency?: string
  pricingUpdatedAt?: string
  /** True when live pricing hasn't loaded yet — cost figures render as skeletons. */
  pricingUnavailable?: boolean
  firstPromptToday?: number
  lastPromptToday?: number
  account?: { email: string | null; name: string | null; plan: string | null }
  usage?: { fiveHour: number; sevenDay: number; fiveHourResetsAt: string | null; sevenDayResetsAt: string | null; plan: string | null }
  /** Usage % over time (Codex source: harvested from rollout rate_limits) — feeds the shared UsageChart. */
  usageHistory?: UsageSnapshot[]
}
export interface GitRepoStat {
  name: string
  commits: number
  added: number
  removed: number
}
/** A row from the cloud `codeflows_usage` table (one hour × source × project × model). */
export interface UsageRowRemote {
  hour_ts: string
  source: 'claude' | 'codex' | 'grok'
  project: string
  model: string
  input_tokens: number
  output_tokens: number
  cache_create_tokens: number
  cache_read_tokens: number
  cost_gbp: number
  prompts: number
  assistant_turns: number
  tool_calls: Record<string, number>
}

/** Outcome of the last cloud usage sync attempt. Mirrors `SyncStatus` in
 *  electron/main/usageSync.ts — kept in sync by hand, like the other IPC types. */
export interface UsageSyncStatus {
  at: string
  outcome: 'ok' | 'partial' | 'signed-out' | 'busy' | 'error'
  mode: 'backfill' | 'incremental' | 'manual'
  rowsSent: number
  rowsUpserted: number
  pricingUnavailable: boolean
  failedCollectors: string[]
  error?: string
}

export interface GitStatPeriod {
  totalCommits: number
  added: number
  removed: number
  net: number
  topRepo: GitRepoStat | null
  repos: GitRepoStat[]
}
export interface GitStats {
  generatedAt: number
  today: GitStatPeriod
  yesterday: GitStatPeriod
  week: GitStatPeriod
}
export interface CodexAccountInfo {
  id: string
  isDefault: boolean
  email: string | null
  name: string | null
  plan: string | null
}
export interface CodexLoginState {
  phase: 'starting' | 'waiting' | 'success' | 'error' | 'cancelled'
  url?: string
  message?: string
}
export interface GrokAccountInfo {
  id: string
  isDefault: boolean
  email: string | null
  name: string | null
  plan: string | null
}
export interface GrokLoginState {
  phase: 'starting' | 'waiting' | 'success' | 'error' | 'cancelled'
  url?: string
  message?: string
}
export interface GeminiAccountInfo {
  id: string
  isDefault: boolean
  email: string | null
  name: string | null
  plan: string | null
}
/** Gemini sign-in is a copy/paste OAuth flow (NO_BROWSER): `needsCode` means
 *  the CLI is waiting for the Google authorization code (submitCode). */
export interface GeminiLoginState {
  phase: 'starting' | 'waiting' | 'needsCode' | 'success' | 'error' | 'cancelled'
  url?: string
  message?: string
}
export interface KimiAccountInfo {
  id: string
  isDefault: boolean
  email: string | null
  name: string | null
  plan: string | null
}
/** Kimi sign-in is a browser DEVICE flow (`kimi login --json`): `waiting`
 *  carries the kimi.com authorize_device URL; the flow completes on its own
 *  once the user authorizes (no code paste-back — submitCode is a no-op). */
export interface KimiLoginState {
  phase: 'starting' | 'waiting' | 'success' | 'error' | 'cancelled'
  url?: string
  message?: string
}
export type UsageAccountStatus = 'connected' | 'stale' | 'expired'
export interface UsageAccountState {
  account: UsageAccount
  status: UsageAccountStatus
  latest: UsageResponse | null
  history: UsageSnapshot[]
  lastFetch: { t: number; ok: boolean; status?: number; error?: string } | null
}
export interface ClaudeUsageState { connecting: boolean; accounts: UsageAccountState[] }

// ---- Claude accounts (mirrors electron/main/claudeAccounts.ts) ----
export interface ClaudeAccount {
  id: string
  isDefault: boolean
  orgId: string | null
  orgName: string | null
  email: string | null
  addedAt?: string
}
export type ClaudeLoginPhase = 'starting' | 'waiting' | 'needsCode' | 'success' | 'error' | 'cancelled'
export interface ClaudeLoginState { phase: ClaudeLoginPhase; url?: string; message?: string }

/**
 * Background state of the Claude CLI auto-updater. Mirrored from the
 * main process's `ClaudeUpdater` via `onClaudeUpdateState`.
 */
export interface ClaudeUpdateState {
  currentVersion: string | null
  latestVersion: string | null
  updating: boolean
  error: string | null
  needsReconnect: boolean
  dismissedInSession: boolean
}

/**
 * State of the CLI installer for one of the agent CLIs. Mirrored from the
 * main process's `CliInstaller` via `onCliInstallState`.
 */
export type CliId = 'claude' | 'codex' | 'grok' | 'gemini' | 'kimi'
export interface CliInstallState {
  cli: CliId
  installed: boolean
  installing: boolean
  error: string | null
  progressLine: string
}

/** CLIs covered by the generic auto-updater (Claude has its own module). */
export type UpdaterCliId = 'codex' | 'grok' | 'gemini' | 'kimi'

/**
 * Background state of one non-Claude CLI's auto-updater. Mirrored from the
 * main process's `CliUpdater` via `onCliUpdateState`. `latestVersion` is always
 * null for grok (no registry) — `manualOnly` marks that case.
 */
export interface CliUpdateState {
  cli: UpdaterCliId
  currentVersion: string | null
  latestVersion: string | null
  updating: boolean
  error: string | null
  needsReconnect: boolean
  dismissedInSession: boolean
  manualOnly: boolean
}

export interface ClaudeSearchResult {
  projectPath: string
  sessionId: string
  sessionTitle: string
  role: 'user' | 'assistant' | 'title'
  snippet: string
  matchOffsetInSnippet: number
  fullMessage: string
  ts: string
  source: 'claude' | 'codex' | 'grok'
}

export interface ClaudeContextMessage {
  index: number
  role: 'user' | 'assistant'
  text: string
  ts: string
  isMatch: boolean
}

export interface ClaudeMessageContext {
  before: ClaudeContextMessage[]
  match: ClaudeContextMessage | null
  after: ClaudeContextMessage[]
  totalMessages: number
  hasMoreAbove: boolean
  hasMoreBelow: boolean
}

/** Main-process stall report — see electron/main/event-loop-monitor.ts. */
export interface EventLoopReport {
  worstLagMs: number
  stalls: Array<{ at: number; lagMs: number; duringChannel: string | null }>
  slowHandlers: Array<{ at: number; channel: string; durationMs: number }>
  topChannels: Array<{ channel: string; calls: number; avgMs: number; maxMs: number }>
}

export interface IElectronAPI {
  // ── Sharing, auth and the Supabase bridge ────────────────────────────
  //
  // These four already existed in electron/preload/index.ts and were simply
  // absent from this interface — which is why Sidebar.tsx:162 and ~100 other
  // call sites have been type-erroring against a method that works fine at
  // runtime. Declared here so the share feature is not built on top of that.
  supabaseRpc: <T = unknown>(
    name: string,
    params?: Record<string, unknown>,
  ) => Promise<{ data: T | null; error: { message: string; code?: string } | null }>
  authGetUser: () => Promise<{ id: string; email?: string } | null>
  onAuthUserChanged: (cb: (user: { id: string; email?: string } | null) => void) => () => void
  authStartLogin: () => Promise<void>
  /** The Supabase auth.uid(), or null when supabase-js has no session —
   *  which can be true even while authGetUser() returns a user. */
  supabaseGetUserId: () => Promise<string | null>

  // Diagnostics
  getEventLoopReport: () => Promise<EventLoopReport>

  // Claude Search
  searchClaudeSessions: (query: string) => Promise<ClaudeSearchResult[]>
  getClaudeMessageContext: (
    projectPath: string,
    sessionId: string,
    query: string,
    before: number,
    after: number
  ) => Promise<ClaudeMessageContext>

  // UI State
  getUiState: () => Promise<{ expandedProjects: string[]; activeProjectId: string | null; collapsedNotes?: string[] }>
  saveUiState: (state: { expandedProjects: string[]; activeProjectId: string | null; collapsedNotes?: string[] }) => Promise<void>

  // Tabs
  getTabs: (projectId: string) => Promise<Tab[]>
  saveTabs: (projectId: string, tabs: Tab[]) => Promise<void>
  saveAllTabs: (allTabs: Record<string, Tab[]>) => Promise<void>
  /** Incremental write: only the changed groups, plus groups to remove. */
  saveTabGroups: (groups: Record<string, Tab[]>, dropped: string[]) => Promise<boolean>
  /** Blocking twin of saveTabGroups, for `beforeunload`. Desktop only. */
  saveTabGroupsSync?: (groups: Record<string, Tab[]>, dropped: string[]) => boolean
  getBootState: () => Promise<{
    folders: Record<string, Folder[]>
    codexSessions: Record<string, any[]>
    grokSessions: Record<string, any[]>
    geminiSessions: Record<string, any[]>
    kimiSessions: Record<string, any[]>
    terminals: Record<string, TerminalInstance[]>
    tabs: Record<string, Tab[]>
    sidebarApps: Record<string, any[]>
  }>

  // Projects
  getProjects: () => Promise<Project[]>
  saveProject: (project: Project) => Promise<void>
  deleteProject: (id: string) => Promise<void>

  // Folders
  getFolders: (projectId: string) => Promise<Folder[]>
  saveFolder: (folder: Folder) => Promise<void>
  saveFoldersForProject: (projectId: string, folders: Folder[]) => Promise<void>
  deleteFolder: (projectId: string, folderId: string) => Promise<void>

  // Project Manager
  pmGetState: (projectId: string) => Promise<PmState | null>
  pmSaveState: (state: PmState) => Promise<void>
  pmOpen: (projectId: string, cwd: string, model: PmModel, engineMode: PmEngineMode, accountId?: string, forceFresh?: boolean) => Promise<void>
  pmClose: (projectId: string) => Promise<void>
  pmSendMessage: (projectId: string, text: string) => Promise<void>
  pmSetStopped: (projectId: string, stopped: boolean) => Promise<void>
  pmToolResult: (requestId: string, result: { ok: boolean; result?: unknown; error?: string }) => Promise<void>
  pmWorkerEvent: (projectId: string, sessionId: string, event: 'finished' | 'waiting' | 'exited', detail?: { exitCode?: number; model?: string; cwd?: string; resumeKey?: string; name?: string }) => Promise<void>
  onPmMessage: (callback: (projectId: string, message: PmMessage) => void) => () => void
  onPmToolRequest: (callback: (projectId: string, requestId: string, tool: string, args: Record<string, unknown>) => void) => () => void
  onPmStream: (callback: (projectId: string, textDelta: string) => void) => () => void

  // Sidebar Apps
  getSidebarApps: (projectId: string) => Promise<SidebarApp[]>
  saveSidebarApps: (projectId: string, apps: SidebarApp[]) => Promise<void>

  // Claude Code
  createClaudeSession: (workingDirectory: string, cols: number, rows: number, initialPrompt?: string, enabledMcps?: string[], permissionMode?: PermissionMode, effortLevel?: EffortLevel, accountId?: string, model?: string) => Promise<{ ptyId: string; resumeKey: string }>
  resumeClaudeSession: (workingDirectory: string, resumeKey: string, cols: number, rows: number, accountId?: string, model?: string, effortLevel?: EffortLevel, permissionMode?: PermissionMode) => Promise<string>
  /** Clone a session's conversation into another CLI's native format; returns the new resume key + how many messages were carried over. */
  sessionConvert: (sourceModel: 'claude' | 'codex' | 'grok' | 'gemini' | 'kimi', targetModel: 'claude' | 'codex' | 'grok' | 'gemini' | 'kimi', cwd: string, resumeKey: string, accountId?: string, targetCwd?: string) => Promise<{ newResumeKey: string; messageCount: number; toolCount: number }>
  openUsagePin: (theme?: 'light' | 'dark') => Promise<void>
  closeUsagePin: () => Promise<void>
  setUsagePinContext: (ctx: { source: 'claude' | 'codex' | 'grok'; claudeAccountId: string | null }) => Promise<void>
  getUsagePinContext: () => Promise<{ source: 'claude' | 'codex' | 'grok'; claudeAccountId: string | null }>
  onUsagePinContext: (cb: (ctx: { source: 'claude' | 'codex' | 'grok'; claudeAccountId: string | null }) => void) => () => void
  isUsagePinOpen: () => Promise<boolean>
  onUsagePinState: (cb: (open: boolean) => void) => () => void
  setUsagePinTheme: (theme: 'light' | 'dark') => Promise<void>
  onUsagePinTheme: (cb: (theme: 'light' | 'dark') => void) => () => void

  // Codex Sessions
  getCodexSessions: (projectId: string) => Promise<CodexSession[]>
  saveCodexSession: (session: CodexSession) => Promise<void>
  deleteCodexSession: (projectId: string, sessionId: string) => Promise<void>

  // Codex CLI
  createCodexSession: (workingDirectory: string, cols: number, rows: number, initialPrompt?: string, accountId?: string, model?: string, effortLevel?: string, permissionMode?: string, enabledMcps?: string[]) => Promise<{ ptyId: string; resumeKey: string }>
  resumeCodexSession: (workingDirectory: string, resumeKey: string, cols: number, rows: number, accountId?: string, model?: string, effortLevel?: string, permissionMode?: string) => Promise<string>

  // Grok Sessions
  getGrokSessions: (projectId: string) => Promise<GrokSession[]>
  saveGrokSession: (session: GrokSession) => Promise<void>
  deleteGrokSession: (projectId: string, sessionId: string) => Promise<void>

  // Grok CLI
  createGrokSession: (workingDirectory: string, cols: number, rows: number, initialPrompt?: string, accountId?: string, model?: string, effortLevel?: string, permissionMode?: string, enabledMcps?: string[]) => Promise<{ ptyId: string; resumeKey: string }>
  resumeGrokSession: (workingDirectory: string, resumeKey: string, cols: number, rows: number, accountId?: string, model?: string, effortLevel?: string, permissionMode?: string) => Promise<string>

  // Gemini Sessions
  getGeminiSessions: (projectId: string) => Promise<GeminiSession[]>
  saveGeminiSession: (session: GeminiSession) => Promise<void>
  deleteGeminiSession: (projectId: string, sessionId: string) => Promise<void>

  // Gemini CLI
  createGeminiSession: (workingDirectory: string, cols: number, rows: number, initialPrompt?: string, accountId?: string, model?: string, effortLevel?: string, permissionMode?: string, enabledMcps?: string[]) => Promise<{ ptyId: string; resumeKey: string }>
  resumeGeminiSession: (workingDirectory: string, resumeKey: string, cols: number, rows: number, accountId?: string, model?: string, effortLevel?: string, permissionMode?: string) => Promise<string>

  // Kimi Sessions
  getKimiSessions: (projectId: string) => Promise<KimiSession[]>
  saveKimiSession: (session: KimiSession) => Promise<void>
  deleteKimiSession: (projectId: string, sessionId: string) => Promise<void>

  // Kimi CLI
  createKimiSession: (workingDirectory: string, cols: number, rows: number, initialPrompt?: string, accountId?: string, model?: string, effortLevel?: string, permissionMode?: string, enabledMcps?: string[]) => Promise<{ ptyId: string; resumeKey: string }>
  resumeKimiSession: (workingDirectory: string, resumeKey: string, cols: number, rows: number, accountId?: string, model?: string, effortLevel?: string, permissionMode?: string) => Promise<string>
  killClaudeSession: (ptyId: string) => Promise<void>
  // Check that a filesystem path exists. Used to detect stale worktree
  // paths on folders before passing them as CWD to Claude — if the
  // worktree was removed externally, spawning `claude` with a dead CWD
  // fails the PTY immediately and the user sees an opaque resume error.
  pathExists: (p: string) => Promise<boolean>
  // Retrieve (and clear) a buffered exit code for a newly-spawned PTY.
  // Plugs a race between `pty-exit` being emitted on the main side
  // and the renderer finishing its `ipcRenderer.on` registration.
  getPtyRecentExit: (ptyId: string) => Promise<number | null>

  // Claude CLI auto-updater — 1hr background poll, npm-backed install,
  // plus a reconnect prompt after the version bumps.
  claudeUpdateGetState: () => Promise<ClaudeUpdateState>
  claudeUpdateCheckNow: () => Promise<ClaudeUpdateState>
  claudeUpdateInstall: () => Promise<ClaudeUpdateState>
  claudeUpdateDismiss: () => Promise<ClaudeUpdateState>
  claudeUpdateClearReconnect: () => Promise<ClaudeUpdateState>
  claudeUpdateManualCommand: () => Promise<string>
  claudeUpdateRunManualCommand: () => Promise<ClaudeUpdateState>
  onClaudeUpdateState: (callback: (state: ClaudeUpdateState) => void) => () => void

  // CLI installer — detects a missing claude/codex/grok binary and installs
  // it via that vendor's official installer, streaming progress pushes.
  /**
   * Live per-CLI model/effort capabilities. `get()` is instant — served from
   * a stale-while-revalidate cache (persisted across launches) while a
   * background probe runs; `onAgentCapabilitiesUpdated` fires if that probe
   * finds something new. `refresh()` forces a fresh probe (manual button).
   */
  agentCapabilities: {
    get: () => Promise<AgentCapabilities>
    refresh: () => Promise<AgentCapabilities>
  }
  onAgentCapabilitiesUpdated: (callback: (capabilities: AgentCapabilities) => void) => () => void
  cliInstallGetState: (cli: CliId) => Promise<CliInstallState>
  cliInstallStart: (cli: CliId) => Promise<CliInstallState>
  cliInstallCommand: (cli: CliId) => Promise<string | null>
  onCliInstallState: (callback: (state: CliInstallState) => void) => () => void

  // Generic CLI updater (codex / grok / gemini / kimi) — 30min background
  // npm/PyPI poll, per-CLI update command on confirm, reconnect prompt after a
  // real version bump. Grok has no registry → manual reinstall only.
  cliUpdateGetState: (cli: UpdaterCliId) => Promise<CliUpdateState>
  cliUpdateGetAll: () => Promise<CliUpdateState[]>
  cliUpdateCheckNow: () => Promise<CliUpdateState[]>
  cliUpdateInstall: (cli: UpdaterCliId) => Promise<CliUpdateState>
  cliUpdateDismiss: (cli: UpdaterCliId) => Promise<CliUpdateState>
  cliUpdateClearReconnect: (cli: UpdaterCliId) => Promise<CliUpdateState>
  cliUpdateManualCommand: (cli: UpdaterCliId) => Promise<string | null>
  cliUpdateRunManualCommand: (cli: UpdaterCliId) => Promise<CliUpdateState>
  onCliUpdateState: (callback: (state: CliUpdateState) => void) => () => void
  claudeUsage: {
    getState: () => Promise<ClaudeUsageState>
    refresh: (accountId?: string) => Promise<ClaudeUsageState>
    clearHistory: (accountId: string) => Promise<ClaudeUsageState>
    onUpdate: (cb: (state: ClaudeUsageState) => void) => () => void
  }
  claudeAccounts: {
    list: () => Promise<ClaudeAccount[]>
    login: () => Promise<{ ok: boolean; account?: ClaudeAccount; error?: string; duplicate?: boolean; existingId?: string; label?: string }>
    submitCode: (code: string) => Promise<void>
    cancelLogin: () => Promise<void>
    remove: (id: string) => Promise<ClaudeAccount[]>
    resolveDuplicateLogin: () => Promise<void>
    onLoginState: (cb: (state: ClaudeLoginState) => void) => () => void
    onChanged: (cb: () => void) => () => void
  }
  localStats: {
    get: (force?: boolean) => Promise<LocalStats>
    getProject: (name: string, force?: boolean) => Promise<LocalStats>
  }
  gitStats: {
    get: (force?: boolean) => Promise<GitStats>
  }
  usageSync: {
    syncNow: () => Promise<UsageSyncStatus>
    getStatus: () => Promise<UsageSyncStatus | null>
    getRange: (fromTs: string, toTs: string, source?: string) => Promise<UsageRowRemote[]>
  }
  codexStats: {
    get: (accountId?: string, force?: boolean) => Promise<LocalStats>
    getProject: (name: string, accountId?: string, force?: boolean) => Promise<LocalStats>
    discoverAgents: (cwd: string) => Promise<DiscoveredAgent[]>
  }
  codexAccounts: {
    list: () => Promise<CodexAccountInfo[]>
    getSelected: () => Promise<string>
    setSelected: (id: string) => Promise<CodexAccountInfo[]>
    login: () => Promise<{ ok: boolean; account?: CodexAccountInfo; error?: string; duplicate?: boolean; existingId?: string; label?: string }>
    cancelLogin: () => Promise<void>
    remove: (id: string) => Promise<CodexAccountInfo[]>
    resolveDuplicateLogin: () => Promise<void>
    onLoginState: (callback: (state: CodexLoginState) => void) => () => void
    onChanged: (cb: () => void) => () => void
  }
  grokStats: {
    get: (accountId?: string, force?: boolean) => Promise<LocalStats>
    getProject: (name: string, accountId?: string, force?: boolean) => Promise<LocalStats>
    discoverAgents: (cwd: string) => Promise<DiscoveredAgent[]>
  }
  grokAccounts: {
    list: () => Promise<GrokAccountInfo[]>
    getSelected: () => Promise<string>
    setSelected: (id: string) => Promise<GrokAccountInfo[]>
    login: () => Promise<{ ok: boolean; account?: GrokAccountInfo; error?: string; duplicate?: boolean; existingId?: string; label?: string }>
    cancelLogin: () => Promise<void>
    remove: (id: string) => Promise<GrokAccountInfo[]>
    resolveDuplicateLogin: () => Promise<void>
    onLoginState: (callback: (state: GrokLoginState) => void) => () => void
    onChanged: (cb: () => void) => () => void
  }
  geminiStats: {
    get: (accountId?: string, force?: boolean) => Promise<LocalStats>
    getProject: (name: string, accountId?: string, force?: boolean) => Promise<LocalStats>
    discoverAgents: (cwd: string) => Promise<DiscoveredAgent[]>
  }
  geminiAccounts: {
    list: () => Promise<GeminiAccountInfo[]>
    getSelected: () => Promise<string>
    setSelected: (id: string) => Promise<GeminiAccountInfo[]>
    login: () => Promise<{ ok: boolean; account?: GeminiAccountInfo; error?: string; duplicate?: boolean; existingId?: string; label?: string }>
    submitCode: (code: string) => Promise<void>
    addApiKey: (key: string, accountId?: string) => Promise<{ ok: boolean; account?: GeminiAccountInfo; error?: string }>
    cancelLogin: () => Promise<void>
    remove: (id: string) => Promise<GeminiAccountInfo[]>
    resolveDuplicateLogin: () => Promise<void>
    onLoginState: (callback: (state: GeminiLoginState) => void) => () => void
    onChanged: (cb: () => void) => () => void
  }
  kimiStats: {
    get: (accountId?: string, force?: boolean) => Promise<LocalStats>
    getProject: (name: string, accountId?: string, force?: boolean) => Promise<LocalStats>
    discoverAgents: (cwd: string) => Promise<DiscoveredAgent[]>
  }
  kimiAccounts: {
    list: () => Promise<KimiAccountInfo[]>
    getSelected: () => Promise<string>
    setSelected: (id: string) => Promise<KimiAccountInfo[]>
    login: (nickname?: string) => Promise<{ ok: boolean; account?: KimiAccountInfo; error?: string; duplicate?: boolean; existingId?: string; label?: string }>
    submitCode: (code: string) => Promise<void>
    cancelLogin: () => Promise<void>
    remove: (id: string) => Promise<KimiAccountInfo[]>
    resolveDuplicateLogin: () => Promise<void>
    onLoginState: (callback: (state: KimiLoginState) => void) => () => void
    onChanged: (cb: () => void) => () => void
  }
  writeToPty: (ptyId: string, data: string) => Promise<void>
  resizePty: (ptyId: string, cols: number, rows: number) => Promise<void>
  getPathForFile: (file: File) => string
  onPtyData: (callback: (ptyId: string, data: string) => void) => () => void
  onPtyDataFor: (ptyId: string, callback: (ptyId: string, data: string) => void) => () => void
  /** `intentional` = CodeFlows killed this pty (tab close, quit, account
   *  switch). False means it died on its own — crashed, or reaped from
   *  outside the app. See app/src/lib/ptyExit.ts. */
  onPtyExit: (callback: (ptyId: string, code: number, intentional: boolean) => void) => () => void
  onCodexSessionId: (callback: (ptyId: string, sessionId: string) => void) => () => void

  // Terminals (persistence)
  getTerminals: (projectId: string) => Promise<TerminalInstance[]>
  saveTerminal: (terminal: TerminalInstance) => Promise<void>
  deleteTerminal: (projectId: string, terminalId: string) => Promise<void>

  // Plain Terminal (PTY)
  createTerminal: (workingDirectory: string, cols: number, rows: number, terminalId?: string, shell?: string) => Promise<string>
  saveTerminalHistory: (workingDirectory: string, terminalId: string, history: string) => Promise<void>
  loadTerminalHistory: (workingDirectory: string, terminalId: string) => Promise<string>
  deleteTerminalHistory: (workingDirectory: string, terminalId: string) => Promise<void>

  // MCP & Skills
  getMcpServers: (workingDirectory: string) => Promise<McpServer[]>
  /** Per-agent configured MCP servers for the session-start MCP picker. Each CLI
   *  reads its OWN config (codex/grok config.toml, gemini settings.json, kimi
   *  mcp.json); 'claude' (or unknown) falls back to getMcpServers. */
  getAgentMcpServers: (agent: 'claude' | 'codex' | 'grok' | 'gemini' | 'kimi', workingDirectory: string) => Promise<McpServer[]>

  getSkills: (workingDirectory: string) => Promise<Skill[]>
  getSlashCommands: (workingDirectory: string) => Promise<SlashCommand[]>

  // AI Sync (Claude ↔ Codex)
  getSyncStatus: (workingDirectory?: string) => Promise<SyncStatus>
  shareMcp: (
    name: string,
    scope: 'global' | 'project',
    direction: 'claude-to-codex' | 'codex-to-claude' | 'claude-to-grok' | 'grok-to-claude',
    workingDirectory?: string,
  ) => Promise<{ success: boolean; error?: string }>
  shareSkill: (
    slug: string,
    scope: 'global' | 'project',
    direction: 'claude-to-codex' | 'codex-to-claude' | 'claude-to-grok' | 'grok-to-claude',
    workingDirectory?: string,
  ) => Promise<{ success: boolean; error?: string }>
  convertSlashCommandToSkill: (
    name: string,
  ) => Promise<{ success: boolean; slug?: string; error?: string }>
  syncMcpAll: (name: string, scope: 'global' | 'project', workingDirectory?: string) => Promise<{ success: boolean; error?: string }>
  setMcpVersion: (name: string, scope: 'global' | 'project', version: string, workingDirectory?: string) => Promise<{ success: boolean; error?: string }>
  getMcpVersions: (npmPackage: string) => Promise<{ latest: string | null; versions: string[]; error?: string }>

  // CLI wrapper (Settings → AI Sync)
  cliWrapperStatus: () => Promise<{
    installed: boolean
    path?: string
    pointsToThisApp: boolean
    reason?: string
    userOptedOut: boolean
  }>
  cliWrapperInstall: () => Promise<{ success: boolean; path?: string; error?: string }>
  cliWrapperUninstall: () => Promise<{ success: boolean; path?: string; error?: string }>

  // Registry
  getRegistryMcps: () => Promise<RegistryMcp[]>
  getRegistrySkills: () => Promise<RegistrySkill[]>
  getRegistryPackages: () => Promise<RegistryPackage[]>
  getRegistryPackage: (id: string) => Promise<RegistryPackageWithContents>
  getRegistryMcp: (id: string) => Promise<RegistryMcp>
  getRegistrySkill: (id: string) => Promise<RegistrySkill>
  trackRegistryInstall: (type: 'mcp' | 'skill' | 'package', id: string) => Promise<void>
  installMcp: (options: {
    name: string
    transport?: 'stdio' | 'http' | 'sse'
    command?: string
    url?: string
    headers?: Record<string, string>
    args: string[]
    envVars: Record<string, string>
    scope: 'global' | 'project'
    workingDirectory?: string
    registryId?: string
    registryVersion?: string
    packageId?: string
    color?: string
    icon?: string
    version?: string
    readme?: string
  }) => Promise<{ success: boolean; error?: string }>
  uninstallMcp: (name: string, scope: 'global' | 'project', workingDirectory?: string) => Promise<{ success: boolean; error?: string }>
  mcpAuth: {
    probe: (url: string) => Promise<'needs-oauth' | 'open' | 'unknown'>
    /** `accountId` scopes the `claude` entry of the result to one Claude
     *  account, the same ids `login` takes. Omitted, it reports the default
     *  account exactly as before this parameter existed. */
    status: (
      name: string,
      url: string,
      accountId?: string
    ) => Promise<Record<McpAuthCli, McpAuthState>>
    /** `workingDirectory` scopes the login child to a project's own config —
     *  omit it for a global-scope server. `accountId` scopes a Claude login
     *  to a specific non-default account; omitted, it targets the default
     *  account exactly as before this parameter existed. */
    login: (
      cli: 'claude' | 'codex',
      name: string,
      workingDirectory?: string,
      accountId?: string
    ) => Promise<{ ok: boolean; error?: string }>
    cancel: () => Promise<void>
    onLoginState: (cb: (state: McpLoginState) => void) => () => void
    /** `missingClis` names the CLIs that don't have the server yet, so the
     *  sign-in dialog can offer to mirror it before running the login chain. */
    onExternalAdd: (cb: (info: { name: string; url: string; missingClis?: Array<'codex' | 'grok'> }) => void) => () => void
    dismissGap: (accountId: string | undefined, serverName: string) => Promise<void>
    clearGapDismissal: (accountId: string | undefined, serverName: string) => Promise<void>
    onAccountGap: (cb: (info: McpAuthAccountGap) => void) => () => void
  }
  installSkill: (options: {
    slug: string
    name: string
    description: string
    body: string
    files?: Array<{ path: string; content: string }>
    scope: 'global' | 'project'
    workingDirectory?: string
    registryId?: string
    registryVersion?: string
    packageId?: string
    color?: string
    icon?: string
    version?: string
    readme?: string
  }) => Promise<{ success: boolean; error?: string }>
  uninstallSkill: (filePath: string) => Promise<{ success: boolean; error?: string }>
  installPackage: (options: {
    packageId: string
    packageSlug: string
    packageName: string
    packageDescription: string
    scope: 'global' | 'project'
    workingDirectory?: string
    skills: Array<{
      id: string
      slug: string
      name: string
      description: string
      content: string
      files?: Array<{ path: string; content: string }>
    }>
    mcps: Array<{
      id: string
      name: string
      command: string
      args: string[]
      envVars: Record<string, string>
    }>
  }) => Promise<{
    success: boolean
    installed: { skills: string[]; mcps: string[] }
    failed: Array<{ type: 'skill' | 'mcp'; name: string; error: string }>
  }>
  uninstallPackage: (
    packageId: string,
    scope: 'global' | 'project',
    workingDirectory?: string
  ) => Promise<{ success: boolean; removed: { skills: number; mcps: number }; error?: string }>
  getInstalledPackages: (workingDirectory?: string) => Promise<InstalledPackage[]>
  readSkillFile: (filePath: string) => Promise<string>
  writeSkillFile: (filePath: string, content: string) => Promise<void>
  readSkillBundledFile: (skillDirectory: string, relPath: string) => Promise<{ success: boolean; content?: string; error?: string }>
  updateSkillFiles: (options: { skillDirectory: string; upsert?: Array<{ path: string; content: string }>; remove?: string[] }) => Promise<{ success: boolean; error?: string }>
  pickFilesForSkill: () => Promise<Array<{ path: string; content: string }>>
  publishSkill: (data: { name: string; slug: string; description: string; content: string; category: string; icon: string; files?: Array<{ path: string; content: string }> }) => Promise<RegistrySkill>
  publishMcp: (data: { name: string; slug: string; description: string; command: string; args: string[]; env_vars: Array<{ key: string; label: string; required: boolean }>; category: string; icon: string; npm_package: string | null; documentation_url: string | null }) => Promise<RegistryMcp>
  publishPackage: (data: { name: string; slug: string; description: string; icon: string; category: string; skill_ids: string[]; mcp_ids: string[] }) => Promise<RegistryPackage>

  // Discovery
  discoverAgents: (workingDirectory: string) => Promise<DiscoveredAgent[]>
  getSessionConversation: (workingDirectory: string, sessionId: string) => Promise<Array<{ role: string; content: string; timestamp: string }>>
  takeoverAgent: (agent: DiscoveredAgent, workingDirectory: string) => Promise<string>
  watchSessionsDirectory: (workingDirectory: string) => Promise<void>
  unwatchSessionsDirectory: (workingDirectory: string) => Promise<void>
  onSessionsChanged: (callback: (workingDirectory: string) => void) => () => void

  // File system
  readDirectory: (dirPath: string, showHidden?: boolean) => Promise<FileNode[]>
  getGitIgnored: (dirPath: string, paths: string[]) => Promise<string[]>
  searchFiles: (workDir: string, query: string, options?: { caseSensitive?: boolean; wholeWord?: boolean; regex?: boolean; include?: string; exclude?: string; excludeHidden?: boolean; excludeGitIgnored?: boolean }) => Promise<{
    results: Array<{ file: string; fullPath: string; matches: Array<{ line: number; text: string }>; matchCount: number }>;
    totalMatches: number; totalFiles: number;
  }>
  replaceInFile: (filePath: string, search: string, replace: string, options?: { caseSensitive?: boolean; wholeWord?: boolean; regex?: boolean; lines?: number[] }) => Promise<{ success: boolean; error?: string }>
  showInFolder: (filePath: string) => Promise<void>
  renameFile: (oldPath: string, newName: string) => Promise<string>
  trashFile: (filePath: string) => Promise<void>
  copyPath: (src: string, dest: string, conflictStrategy: 'rename' | 'replace' | 'skip') => Promise<{ success: boolean; destPath: string | null }>
  movePath: (src: string, dest: string, conflictStrategy: 'rename' | 'replace' | 'skip') => Promise<{ success: boolean; destPath: string | null }>
  checkPathsExist: (paths: string[]) => Promise<string[]>
  createFile: (filePath: string) => Promise<string>
  createFolder: (dirPath: string) => Promise<string>
  readFile: (filePath: string) => Promise<string>
  getFileMediaType: (filePath: string) => Promise<string | null>
  selectDirectory: () => Promise<string | null>

  // Temp images (AiBoards export → file path that Claude can Read)
  saveTempImage: (workingDirectory: string, bucket: string, dataUrl: string) => Promise<{ success: boolean; path?: string; error?: string }>
  deleteTempImage: (filePath: string) => Promise<{ success: boolean; error?: string }>

  // Project icons
  saveProjectIconImage: (
    projectId: string,
    payload: { rendered: ArrayBuffer; source: ArrayBuffer }
  ) => Promise<{ success: boolean; path?: string; error?: string }>
  getProjectIconPath: (projectId: string) => Promise<string | null>
  getProjectIconDataUrl: (projectId: string) => Promise<string | null>
  deleteProjectIconImage: (projectId: string) => Promise<void>
  listRecentIcons: () => Promise<RecentIcon[]>
  useRecentIcon: (projectId: string, sha: string) => Promise<{ success: boolean; error?: string }>
  getProjectIconSourceDataUrl: (projectId: string) => Promise<string | null>
  removeRecentIcon: (sha: string) => Promise<{ success: boolean; error?: string }>

  // Open With (Finder/Explorer)
  resolveOpenPath: (p: string) => Promise<{
    workingDirectory: string
    filePath: string
    isFile: boolean
    expandedDirs: string[]
    suggestedName: string
  } | null>
  onOpenWithPath: (callback: (filePath: string) => void) => () => void

  // Ports
  detectPorts: (workingDirectory: string) => Promise<PortInfo[]>

  // Notes
  getNotes: (workingDirectory: string) => Promise<NoteFile[]>
  readNote: (filePath: string) => Promise<string>
  readNoteStat: (filePath: string) => Promise<number | null>
  saveNote: (filePath: string, content: string) => Promise<void>
  createNote: (workingDirectory: string, name: string) => Promise<NoteFile>
  deleteNote: (filePath: string) => Promise<void>
  renameNote: (oldPath: string, newName: string) => Promise<NoteFile>
  watchNotesDirectory: (workingDirectory: string) => Promise<void>
  unwatchNotesDirectory: (workingDirectory: string) => Promise<void>
  onNotesChanged: (callback: (workingDirectory: string) => void) => () => void

  // Editor State
  editorUpdateSelection: (file: string, text: string, range: unknown, projectDir?: string) => Promise<void>
  editorUpdateSelections: (selections: Array<{ file: string; text: string; range?: unknown }>, projectDir?: string) => Promise<void>
  editorUpdateActiveFile: (file: string, projectDir?: string) => Promise<void>
  editorUpdateOpenFiles: (files: string[], projectDir?: string) => Promise<void>
  editorUpdateWorkspaces: (folders: string[], projectDir?: string) => Promise<void>
  editorUpdateSplitView: (snapshot: SplitViewSnapshot | null, projectDir?: string) => Promise<void>

  // Pending Selections
  getPendingSelections: () => Promise<Record<string, unknown>>
  savePendingSelections: (selections: Record<string, unknown>) => Promise<void>
  watchConsumed: (projectDir?: string) => Promise<void>
  unwatchConsumed: (projectDir?: string) => Promise<void>
  onSelectionConsumed: (callback: () => void) => () => void

  // Notifications
  showNotification: (title: string, body: string, sessionId?: string) => Promise<void>

  /**
   * Drive CodeFlows' own dock icon badge from the renderer. Pass the
   * current unread notification count; pass 0 to clear. The main
   * process holds an authoritative "intended" value and a sanitiser
   * interval that wipes any divergent badge (e.g. leaks from
   * embedded PWAs' service workers). CodeFlows is the sole legitimate
   * source of dock badge values.
   */
  setCodeflowsBadge: (count: number) => Promise<void>
  onNotificationClick: (callback: (sessionId: string) => void) => () => void

  // Settings
  getSetting: (key: string) => Promise<any>
  setSetting: (key: string, value: unknown) => Promise<void>
  getClaudeCleanupPeriod: () => Promise<number | null>
  setClaudeCleanupPeriod: (
    days: number,
  ) => Promise<{ ok: boolean; value?: number; error?: string }>
  setWindowBackground: (color: string) => Promise<boolean>

  // App Info
  getAppVersion: () => Promise<string>

  // Auto Update
  getAutoUpdate: () => Promise<boolean>
  setAutoUpdate: (enabled: boolean) => Promise<void>
  checkForUpdates: () => Promise<void>
  startUpdate: () => Promise<void>
  applyPendingUpdate: () => Promise<void>
  dismissUpdateVersion: (version: string) => Promise<void>
  cancelUpdate: () => Promise<void>
  onUpdateStatus: (callback: (data: any) => void) => () => void

  // Git
  ghListRepos: () => Promise<Array<{ name: string; owner: { login: string }; url: string; description: string; isPrivate: boolean; updatedAt: string }> | null>
  gitClone: (repoUrl: string, targetDir: string) => Promise<{ success: boolean; error?: string }>
  onGitCloneProgress: (callback: (msg: string) => void) => () => void
  gitIsRepo: (workingDirectory: string) => Promise<boolean>
  gitStatus: (workingDirectory: string) => Promise<GitFileChange[]>
  gitDiff: (workingDirectory: string, filePath: string, staged: boolean) => Promise<string>
  gitDiffAll: (workingDirectory: string) => Promise<string>
  gitBranches: (workingDirectory: string) => Promise<GitBranch[]>
  gitCheckoutBranch: (workingDirectory: string, branch: string) => Promise<{ success: boolean; error?: string }>
  gitCreateBranch: (workingDirectory: string, branch: string) => Promise<{ success: boolean; error?: string }>
  gitStage: (workingDirectory: string, paths: string[]) => Promise<void>
  gitUnstage: (workingDirectory: string, paths: string[]) => Promise<void>
  gitStageAll: (workingDirectory: string) => Promise<void>
  gitUnstageAll: (workingDirectory: string) => Promise<void>
  gitCommit: (workingDirectory: string, message: string) => Promise<{ success: boolean; error?: string }>
  gitPush: (workingDirectory: string) => Promise<{ success: boolean; error?: string }>
  gitPull: (workingDirectory: string) => Promise<{ success: boolean; error?: string }>
  gitFetch: (workingDirectory: string) => Promise<{ success: boolean; error?: string }>
  gitLog: (workingDirectory: string, count?: number) => Promise<GitLogEntry[]>
  gitShow: (workingDirectory: string, hash: string, filePath?: string) => Promise<string>
  gitShowFiles: (workingDirectory: string, hash: string) => Promise<Array<{ path: string; status: string }>>
  gitStashList: (workingDirectory: string) => Promise<GitStashEntry[]>
  gitStashSave: (workingDirectory: string, message?: string) => Promise<{ success: boolean; error?: string }>
  gitStashPop: (workingDirectory: string, index?: number) => Promise<{ success: boolean; error?: string }>
  gitDiscard: (workingDirectory: string, paths: string[]) => Promise<{ success: boolean; error?: string }>
  gitMerge: (workingDirectory: string, branch: string) => Promise<{ success: boolean; output?: string; error?: string }>
  gitWorktreeAdd: (workingDirectory: string, branch: string, sessionId: string) => Promise<{ success: boolean; path?: string; error?: string }>
  gitWorktreeRemove: (workingDirectory: string, worktreePath: string) => Promise<{ success: boolean; error?: string }>
  gitWorktreeList: (workingDirectory: string) => Promise<Array<{ path: string; branch: string }>>

  // Request Data (file persistence)
  saveRequestData: (workingDirectory: string, requestId: string, data: string) => Promise<void>
  loadRequestData: (workingDirectory: string, requestId: string) => Promise<string>
  deleteRequestData: (workingDirectory: string, requestId: string) => Promise<void>

  // Webhook (file persistence)
  saveWebhookState: (workingDirectory: string, tabId: string, data: string) => Promise<void>
  loadWebhookState: (workingDirectory: string, tabId: string) => Promise<string>
  deleteWebhookState: (workingDirectory: string, tabId: string) => Promise<void>
  appendWebhookRequest: (workingDirectory: string, tabId: string, requestJson: string) => Promise<void>
  loadWebhookRequests: (workingDirectory: string, tabId: string, limit?: number) => Promise<string[]>
  clearWebhookRequests: (workingDirectory: string, tabId: string) => Promise<void>

  // HTTP Request
  sendHttpRequest: (config: {
    method: string
    url: string
    headers: Record<string, string>
    body?: string
    followRedirects: boolean
    followOriginalMethod: boolean
    followAuthHeader: boolean
    removeRefererOnRedirect: boolean
    encodeUrl: boolean
    timeout?: number
  }) => Promise<{
    status: number
    statusText: string
    headers: Record<string, string>
    body: string
    bodyBase64?: string
    duration: number
    size: number
  }>

  // Window
  minimizeWindow: () => void
  maximizeWindow: () => void
  closeWindow: () => void
  isMaximized: () => Promise<boolean>
  onMaximizeChange: (callback: (maximized: boolean) => void) => () => void

  // Voice transform
  voiceTransform: (req: { id: string; prompt: string; transcript: string }) => Promise<{ ok: true; text: string } | { ok: false; error: string }>
  voiceCancel: (id: string) => Promise<void>
  voiceTranscribe: (req: { audioBase64: string; mimeType: string; language?: string }) => Promise<{ ok: true; text: string } | { ok: false; error: string }>
  voiceRequestMicAccess: () => Promise<{ granted: boolean; error?: string }>

  // Overlay
  overlayAttachToFrontmost: () => Promise<{ success: boolean; error?: string }>
  overlayDetach: () => Promise<void>
  overlayShow: () => Promise<void>
  overlayHide: () => Promise<void>
  overlayRegisterKeybind: (accelerator: string) => Promise<boolean>
  overlayUnregisterKeybind: () => Promise<void>
  onOverlayStateChanged: (callback: (state: { overlayState: OverlayState }) => void) => () => void
}

declare global {
  interface Window {
    api: IElectronAPI
  }
}
